/** @jsx jsx */

import { isEqual } from 'lodash';
import { ChangeEvent, Fragment, useEffect, useState } from 'react';

import {
  Field,
  Grid,
  LabeledCheckbox,
  LegacyButton,
  Spacer,
  TextInput,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import type { PartnerProfile } from 'app/developer-v3/types/partnerProfile';

const Styles = {
  buttonWrapper: css`
    display: flex;
    justify-content: flex-end;
  `,
};

type Props = {
  initialPartnerProfile: PartnerProfile;
  onUpdate: (partnerProfileNext: Partial<PartnerProfile>) => Promise<void>;
};

function Profile({ initialPartnerProfile, onUpdate }: Props) {
  const [partnerProfile, setPartnerProfile] = useState(initialPartnerProfile);
  const [areFieldsErred, setAreFieldsErred] = useState({
    firstName: false,
    lastName: false,
    roles: false,
  });

  const clearErrors = () =>
    setAreFieldsErred({ firstName: false, lastName: false, roles: false });

  useEffect(() => {
    setPartnerProfile(initialPartnerProfile);
    clearErrors();
  }, [initialPartnerProfile]);

  const hasError =
    areFieldsErred.firstName || areFieldsErred.lastName || areFieldsErred.roles;

  const isDirty =
    partnerProfile.firstName !== initialPartnerProfile.firstName ||
    partnerProfile.jobTitle !== initialPartnerProfile.jobTitle ||
    partnerProfile.lastName !== initialPartnerProfile.lastName ||
    !isEqual(partnerProfile.roles, initialPartnerProfile.roles);

  const hasCheckedRole = partnerProfile.roles.find(({ checked }) => checked);

  return (
    <Fragment>
      <Grid columns={1} gap={35}>
        {!initialPartnerProfile.customuser && (
          <Grid columns={2} gap={25}>
            <Field
              error={areFieldsErred.firstName ? 'required' : undefined}
              isRequired={true}
              label="First Name"
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-describedby="first-name"
                  aria-invalid={!!areFieldsErred.firstName}
                  aria-label="First Name"
                  aria-required={true}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setPartnerProfile({
                      ...partnerProfile,
                      firstName: event.target.value,
                    })
                  }
                  value={partnerProfile.firstName}
                />
              )}
            />
            <Field
              error={areFieldsErred.lastName ? 'required' : undefined}
              isRequired={true}
              label="Last Name"
              renderInput={inputProps => (
                <TextInput
                  {...inputProps}
                  aria-describedby="last-name"
                  aria-invalid={!!areFieldsErred.lastName}
                  aria-label="Last Name"
                  aria-required={true}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    setPartnerProfile({
                      ...partnerProfile,
                      lastName: event.target.value,
                    })
                  }
                  value={partnerProfile.lastName}
                />
              )}
            />
          </Grid>
        )}
        <Field
          error={
            areFieldsErred.roles || !hasCheckedRole
              ? 'Please select at least one.'
              : undefined
          }
          helpTextLineClamp={2}
          isRequired={true}
          label="How are you typically involved with integrations?"
          renderHelpText={() => (
            <p>
              We'll personalize your experience based on the options you select.
              (select all that apply)
            </p>
          )}
          renderInput={() =>
            partnerProfile.roles.map(role => (
              <LabeledCheckbox
                aria-label={role.name}
                checked={role.checked}
                key={role.value}
                name="roles"
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const isChecked = event.target.checked;
                  const rolesNext = partnerProfile.roles.map(roleNext => ({
                    ...roleNext,
                    checked:
                      role.value === roleNext.value
                        ? isChecked
                        : roleNext.checked,
                  }));
                  setPartnerProfile({ ...partnerProfile, roles: rolesNext });
                }}
                tabIndex={0}
                value={role.value}
              >
                {role.name}
              </LabeledCheckbox>
            ))
          }
        />
        <Field
          label="Job Title"
          renderHelpText={() => <p>(optional)</p>}
          renderInput={inputProps => (
            <TextInput
              {...inputProps}
              aria-describedby="job-title"
              aria-invalid={false}
              aria-label="Job Title"
              aria-required={true}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setPartnerProfile({
                  ...partnerProfile,
                  jobTitle: event.target.value,
                })
              }
              value={partnerProfile.jobTitle}
            />
          )}
        />
      </Grid>
      <Spacer height={35} />
      <div css={Styles.buttonWrapper}>
        <LegacyButton
          disabled={!isDirty || !hasCheckedRole}
          onClick={async e => {
            e.preventDefault();
            if (!hasError) {
              onUpdate(partnerProfile);
            }
          }}
        >
          Save Changes
        </LegacyButton>
      </div>
    </Fragment>
  );
}

export default Profile;
